import React from 'react';
import styled from 'styled-components';
import { Button, Dropdown, Icon } from '~/components';
import { useAuth } from '~/hooks';
import { colors, weights } from '~/styles';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  border: 1px solid ${colors.grey10};
  border-radius: 0.5rem;
  cursor: pointer;
  transition: box-shadow 150ms ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const CardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardName = styled.div`
  font-weight: ${weights.bold};
  font-size: 0.875rem;
  color: ${colors.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.5rem;
`;

const ScopeBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: normal;
  color: ${colors.grey40};
  text-transform: uppercase;
  letter-spacing: 0.03rem;
`;

const CardDescription = styled.div`
  font-size: 0.8125rem;
  color: ${colors.grey55};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
  flex-shrink: 0;
`;

const scopeLabels = {
  system: 'Standard',
  workspace: 'Workspace',
  member: 'Personal',
};

export default function AssistantProfileCard({ profile, onView, onEdit, onDelete }) {
  const auth = useAuth();

  const canManage = profile.scopeKey === 'member' || (profile.scopeKey === 'workspace' && auth.workspace.manage);

  const handleCardClick = () => {
    if (canManage) {
      onEdit?.();
    } else {
      onView?.();
    }
  };

  return (
    <Card onClick={handleCardClick}>
      <CardHeader>
        <CardInfo>
          <CardName>{profile.name}</CardName>
          {profile.description && <CardDescription>{profile.description}</CardDescription>}
        </CardInfo>
        <CardActions>
          <ScopeBadge>{scopeLabels[profile.scopeKey]}</ScopeBadge>
          <Dropdown align="right">
            {({ setIsOpen }) => {
              const handleAction = (action, e) => {
                e?.stopPropagation();
                setIsOpen(false);
                action?.();
              };

              return (
                <>
                  <Dropdown.Trigger style={{ display: 'flex' }}>
                    <Button
                      isAnchor
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(true);
                      }}>
                      <Icon icon="ellipsis-h" />
                    </Button>
                  </Dropdown.Trigger>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={(e) => handleAction(onView, e)}>View</Dropdown.Item>
                    {canManage && <Dropdown.Item onClick={(e) => handleAction(onEdit, e)}>Edit</Dropdown.Item>}
                    {canManage && (
                      <Dropdown.DeleteItem onClick={(e) => handleAction(onDelete, e)}>Delete</Dropdown.DeleteItem>
                    )}
                  </Dropdown.Menu>
                </>
              );
            }}
          </Dropdown>
        </CardActions>
      </CardHeader>
    </Card>
  );
}
